import React from "react";
import PolicyLayout, { Section } from "@/components/PolicyLayout";

const SECTIONS = [
  { id: "intro", label: "1. Overview" },
  { id: "coverage", label: "2. Coverage & Serviceability" },
  { id: "timelines", label: "3. Delivery Timelines" },
  { id: "charges", label: "4. Shipping Charges" },
  { id: "tracking", label: "5. Order Tracking" },
  { id: "packaging", label: "6. Packaging" },
  { id: "attempts", label: "7. Delivery Attempts" },
  { id: "address", label: "8. Address Changes & RTO" },
  { id: "verification", label: "9. Delivery Verification & OTP" },
  { id: "risk", label: "10. Risk of Loss" },
  { id: "delays", label: "11. Delays & Force Majeure" },
  { id: "vendor-obligations", label: "12. Vendor Obligations" },
  { id: "grievance", label: "13. Grievance" },
];

export default function ShippingPolicy() {
  return (
    <PolicyLayout
      title="Shipping Policy"
      tagline="Florvanta ships PAN-India through vetted logistics partners. Delivery timelines vary by product category, seller location, and destination pincode."
      updated="February 2026"
      sections={SECTIONS}
    >
      <Section id="intro" title="1. Overview">
        <p>
          Products purchased on Florvanta are fulfilled by the respective Vendor, either through Florvanta&apos;s aggregated logistics network or the Vendor&apos;s own shipping partner. Coordination and tracking happen through the Platform.
        </p>
      </Section>

      <Section id="coverage" title="2. Coverage &amp; Serviceability">
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Clothing &amp; Electronics:</b> shipped across India to all serviceable pincodes.</li>
          <li><b>Grocery:</b> available only in select serviceable pincodes where a local Vendor can fulfil within the delivery window. Serviceability is checked at cart and at checkout.</li>
          <li>Certain remote / restricted areas may be non-serviceable; you will be notified before checkout.</li>
        </ul>
      </Section>

      <Section id="timelines" title="3. Delivery Timelines">
        <div className="overflow-auto rounded-lg border border-[#E2E8E5]">
          <table className="w-full text-sm">
            <thead className="bg-[#E8F5F2] text-[#0F3D2E]">
              <tr>
                <th className="p-3 text-left">Category</th>
                <th className="p-3 text-left">Metro (Tier-1)</th>
                <th className="p-3 text-left">Tier-2/3</th>
                <th className="p-3 text-left">Remote &amp; NE</th>
              </tr>
            </thead>
            <tbody className="text-[#0F3D2E]">
              <tr className="border-t border-[#E2E8E5]"><td className="p-3">Clothing</td><td className="p-3">3-5 days</td><td className="p-3">5-8 days</td><td className="p-3">7-12 days</td></tr>
              <tr className="border-t border-[#E2E8E5]"><td className="p-3">Electronics</td><td className="p-3">3-6 days</td><td className="p-3">5-9 days</td><td className="p-3">7-12 days</td></tr>
              <tr className="border-t border-[#E2E8E5]"><td className="p-3">Grocery (Perishable)</td><td className="p-3">Same day / next day</td><td className="p-3">1-2 days</td><td className="p-3">Not serviceable</td></tr>
              <tr className="border-t border-[#E2E8E5]"><td className="p-3">Grocery (Packaged)</td><td className="p-3">1-3 days</td><td className="p-3">2-5 days</td><td className="p-3">5-8 days</td></tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#6E877C]">Timelines are indicative business days from order confirmation. Individual listings may show a more specific delivery estimate.</p>
      </Section>

      <Section id="charges" title="4. Shipping Charges">
        <ul className="list-disc pl-5 space-y-1">
          <li>Shipping charges (if any) are displayed at cart and again on the checkout page before payment.</li>
          <li>Free shipping may be offered by Vendors above a threshold order value.</li>
          <li>Any COD handling fee, if applicable, will be shown separately.</li>
        </ul>
      </Section>

      <Section id="tracking" title="5. Order Tracking">
        <p>
          Tracking details (AWB / tracking number) are shared via SMS, email, and on <b>My Orders</b>. Multi-vendor orders may be split into multiple parcels shipped independently — each parcel will have its own tracking.
        </p>
      </Section>

      <Section id="packaging" title="6. Packaging">
        <p>
          Vendors are expected to pack items securely, with tamper-evident sealing for high-value and perishable items. If your package appears tampered with, please refuse delivery and immediately raise a concern with a photograph.
        </p>
      </Section>

      <Section id="attempts" title="7. Delivery Attempts">
        <p>
          Our logistics partners will attempt delivery up to <b>three (3)</b> times. If undelivered after three attempts, the order will be marked as Return-To-Origin (RTO) and, for prepaid orders, refunded as per the Refund Policy.
        </p>
      </Section>

      <Section id="address" title="8. Address Changes &amp; RTO">
        <p>
          Address / phone changes are possible only before the order is marked <b>Shipped</b>. Once shipped, changes are subject to the courier&apos;s policy and may not be possible. Please ensure your address, pincode, and phone number are accurate.
        </p>
      </Section>

      <Section id="verification" title="9. Delivery Verification &amp; OTP">
        <p>
          For high-value orders and certain electronics, our logistics partners may share a delivery OTP with you. Please share this OTP with the delivery agent <b>only at the time of receiving your parcel</b>. Do not share it with anyone otherwise.
        </p>
      </Section>

      <Section id="risk" title="10. Risk of Loss">
        <p>
          Risk of loss and title passes to the Buyer upon delivery and acceptance of the item. Damage or loss reported after acceptance is treated under the <a className="text-[#14A085] underline" href="/return-policy">Return Policy</a>.
        </p>
      </Section>

      <Section id="delays" title="11. Delays &amp; Force Majeure">
        <p>
          Florvanta and its logistics partners shall not be liable for delivery delays caused by force majeure events including natural disasters, strikes, riots, pandemics, government orders, court injunctions, or courier network outages.
        </p>
      </Section>

      <Section id="vendor-obligations" title="12. Vendor Obligations">
        <ul className="list-disc pl-5 space-y-1">
          <li>Ship every order within the vendor SLA (typically within 24-72 hours of order confirmation).</li>
          <li>Update dispatch and tracking on the Platform within the SLA.</li>
          <li>Include the Vendor tax invoice inside the parcel, in compliance with the CGST Act, 2017.</li>
          <li>Comply with the Legal Metrology (Packaged Commodities) Rules, 2011 for weight and MRP labelling.</li>
        </ul>
      </Section>

      <Section id="grievance" title="13. Grievance">
        <p>Shipping complaints: <b>support@florvanta.co.in</b>. Unresolved after 15 days: <b>grievance@florvanta.co.in</b>.</p>
      </Section>
    </PolicyLayout>
  );
}
